import type { ParamStore } from '../graph/nodeDefs.js';
import { mergeParamOverrides } from '../graph/paramKeys.js';
import type { Scene, SceneNodeId, LayerNode } from '../scene/sceneState.js';
import type { LayoutResult, NodeLayout, SceneLayoutContext } from '../scene/layoutEngine.js';
import { computeLayout, nodePath, placementPrefix, shownInSession } from '../scene/layoutEngine.js';
import type { LayerDef } from '../scene/layerState.js';
import type { GraphRuntime, Space } from '../graph/graphRuntime.js';
import { mergeRuntimeParamOverrides } from './runtimeParamPrecedence.js';
import { NO_CLIP, clipWithin, type SceneClip } from '../scene/clip.js';

export interface SceneDrawItem {
  layerNode: LayerNode;
  layerDef: LayerDef;
  layout: NodeLayout;
  runtime?: GraphRuntime;
  /** What the clipping frames above clip the layer to: a scissor, and its box where a frame is turned. */
  clip: SceneClip;
  paramOverrides: Record<string, unknown>;
}

export function collectSceneDrawItems(
  scene: Scene,
  layoutResult: LayoutResult,
  layoutContext: SceneLayoutContext,
  layerLibrary: Record<string, LayerDef>,
  ids: SceneNodeId[],
  out: SceneDrawItem[],
  ancestors: Set<string>,
  clip: SceneClip = NO_CLIP,
  pathPrefix = '',
  inheritedSerialized: Record<string, unknown> = {},
  inheritedRuntime: Record<string, unknown> = {},
): void {
  for (const id of ids) {
    const node = scene.nodes[id];
    if (!node) continue;
    const path = nodePath(pathPrefix, id);
    if (!shownInSession(layoutContext.session, path, node.visible)) continue;
    const ownerRuntime = layoutContext.behaviorParams?.[path] ?? {};
    if (node.kind === 'layer') {
      const layout = layoutResult.layoutMap[id];
      const layerDef = layerLibrary[node.layerId];
      const runtime = layoutContext.resolveRuntime?.(node.layerId) ?? undefined;
      if (layout && layerDef) out.push({
        layerNode: node,
        layerDef,
        layout,
        runtime,
        clip,
        paramOverrides: mergeRuntimeParamOverrides(
          inheritedSerialized,
          node.paramOverrides,
          inheritedRuntime,
          ownerRuntime,
        ),
      });
    } else if (node.kind === 'frame') {
      const frameLayout = node.clip ? layoutResult.layoutMap[id] : undefined;
      const childClip = frameLayout ? clipWithin(clip, frameLayout) : clip;
      collectSceneDrawItems(scene, layoutResult, layoutContext, layerLibrary, node.childIds, out, ancestors, childClip, pathPrefix, inheritedSerialized, { ...inheritedRuntime, ...ownerRuntime });
    } else if (node.kind === 'instance') {
      if (ancestors.has(node.componentId)) continue;
      const componentDef = layoutContext.componentLibrary[node.componentId];
      const outerLayout = layoutResult.layoutMap[id];
      if (!componentDef || !outerLayout) continue;
      const innerSpace: Space = { min: { w: 0, h: 0 }, max: { w: outerLayout.localBounds.w, h: outerLayout.localBounds.h } };
      const innerLayout = computeLayout(componentDef.scene, innerSpace, {
        ...layoutContext,
        parentTransform: outerLayout.fromLocal,
        behaviorPathPrefix: placementPrefix(path),
      });
      collectSceneDrawItems(
        componentDef.scene,
        innerLayout,
        layoutContext,
        layerLibrary,
        componentDef.scene.rootIds,
        out,
        new Set([...ancestors, node.componentId]),
        clip,
        placementPrefix(path),
        { ...inheritedSerialized, ...node.paramOverrides },
        { ...inheritedRuntime, ...ownerRuntime },
      );
    }
  }
}

export function mergeLayerParams(baseParams: ParamStore, overrides: Record<string, unknown>): ParamStore {
  return mergeParamOverrides(baseParams, overrides);
}
